import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * TeamAvatars — stacked initials-on-tinted-background avatars used on the
 * clients table (T-0108).
 *
 * v1 visual contract — mirrors `.avatar` and `.avatar-stack` in
 * `design_handoff_caliber_v2/prototype/tokens.css`:
 *
 *   - 32px circle (the ticket pins 32px; the prototype's default is 24px,
 *     but the ticket wins per CLAUDE.md §3).
 *   - 1px white border so the avatars read as separate disks even when
 *     overlapped.
 *   - 11px / 600 / mono initials in the per-tone ink color.
 *   - Stacked with -8px overlap (the prototype uses -6px on 24px circles;
 *     -8px keeps the same relative overlap on a 32px circle).
 *   - First four (default) members render as avatars; remaining as a
 *     `+N more` chip in the same row.
 *
 * Tone palette: six bg/ink pairs declared in `apps/web/src/globals.css`
 * (`--avatar-{1..6}-{bg,ink}`) that mirror `.avatar-{1..6}` from the
 * prototype tokens. We don't pick from `<ProvenanceChip>`'s palette as the
 * ticket prompt suggests — that primitive uses only `ink-100`/`paper-100`
 * (a *neutral* palette by design) — so we use the dedicated avatar palette
 * the prototype ships with. The selection is deterministic per name
 * (`hashIndex`) so the same person renders the same color across reloads.
 *
 * Accessibility: the wrapper is a `<ul>` with `aria-label` so screen
 * readers announce "Engagement team, 5 members." Each avatar carries the
 * member's display name as the native `title`.
 */

export interface TeamMember {
  /** Stable id (user_id from the backend). */
  id: string;
  /** Display name — e.g. "Sarah Chen". */
  name: string;
  /** Pre-computed initials. Optional; falls back to deriving from `name`. */
  initials?: string;
}

export interface TeamAvatarsProps extends React.HTMLAttributes<HTMLUListElement> {
  members: ReadonlyArray<TeamMember>;
  /**
   * Maximum avatars to render before collapsing the remainder into a
   * `+N` chip. The ticket pins this at 4.
   */
  max?: number;
  /** Pixel diameter of each avatar. The ticket pins this at 32px. */
  size?: number;
}

/**
 * Map any string to a stable 1..6 index — used to pick an avatar tone
 * deterministically from the user's name/id. djb2 is overkill for ASCII
 * names but the cost is trivial and the distribution is good enough.
 */
function hashIndex(s: string, buckets: number): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = (h * 33) ^ s.charCodeAt(i);
  }
  // Coerce to unsigned and modulo into 1..buckets.
  return (Math.abs(h) % buckets) + 1;
}

/** Default initials derivation — first letter of first two words, uppercase. */
function deriveInitials(name: string): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]!.charAt(0) ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]!.charAt(0) ?? '') : '';
  return (first + second).toUpperCase() || trimmed.charAt(0).toUpperCase();
}

/** Pick the avatar tone (1..6) from a stable hash of the member's id+name. */
export function avatarTone(member: Pick<TeamMember, 'id' | 'name'>): number {
  // id + name keeps the choice stable even if the name updates after a
  // re-render but the id is the same; falling back to name alone when id
  // is empty.
  const key = (member.id ?? '').trim() || (member.name ?? '');
  return hashIndex(key, 6);
}

export function TeamAvatars({ members, max = 4, size = 32, className, ...rest }: TeamAvatarsProps) {
  if (!members || members.length === 0) {
    return (
      <span className="text-meta text-ink-500" aria-label="No team members assigned">
        —
      </span>
    );
  }

  const visible = members.slice(0, max);
  const remainder = members.length - visible.length;
  // -8px overlap matches the prototype's avatar-stack rule scaled to
  // the 32px size. The CSS variable lets the size+overlap stay in sync
  // if a consumer overrides `size`.
  const overlap = Math.round(size / 4);

  return (
    <ul
      className={cn('flex items-center', className)}
      aria-label={`Engagement team, ${members.length} member${members.length === 1 ? '' : 's'}`}
      style={{ paddingInlineStart: 0, listStyle: 'none' }}
      {...rest}
    >
      {visible.map((m, idx) => {
        const tone = avatarTone(m);
        const initials = m.initials ?? deriveInitials(m.name);
        return (
          <li
            key={m.id || `${m.name}-${idx}`}
            className="m-0 p-0"
            style={idx === 0 ? undefined : { marginInlineStart: -overlap }}
          >
            <span
              title={m.name}
              aria-label={m.name}
              className={cn(
                'inline-flex items-center justify-center rounded-full border border-paper-0',
                'font-mono font-semibold',
                `bg-avatar-${tone}-bg`,
                `text-avatar-${tone}-ink`,
              )}
              style={{
                width: size,
                height: size,
                fontSize: Math.round(size * 0.34),
                lineHeight: 1,
              }}
            >
              {initials}
            </span>
          </li>
        );
      })}
      {remainder > 0 ? (
        <li className="m-0 p-0" style={{ marginInlineStart: -overlap }}>
          <span
            className="inline-flex items-center justify-center rounded-full border border-paper-0 bg-ink-100 font-mono font-semibold text-ink-700"
            style={{
              width: size,
              height: size,
              fontSize: Math.round(size * 0.32),
              lineHeight: 1,
            }}
            title={`${remainder} more team member${remainder === 1 ? '' : 's'}`}
            aria-label={`Plus ${remainder} more`}
          >
            +{remainder}
          </span>
        </li>
      ) : null}
    </ul>
  );
}

TeamAvatars.displayName = 'TeamAvatars';

export { deriveInitials, hashIndex };
